import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { FiFolder, FiFile, FiUpload, FiTrash2, FiDownload, FiFolderPlus, FiX, FiSearch, FiEye, FiEdit2, FiMove } from "react-icons/fi";
import Toast from "./Toast";

import { API_URL, getAssetUrl } from "../config";

/** Authenticated file bytes via API — avoids nginx /uploads misconfig returning index.html (breaks DOCX/ZIP parsing). */
const getFileStreamUrl = (fileId) => `${API_URL}/files/${fileId}/stream`;

function authFileFetch(url, init = {}) {
	const token = localStorage.getItem("token");
	return fetch(url, {
		...init,
		headers: {
			...init.headers,
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		credentials: "include",
	});
}

function Files() {
	const { user } = useAuth();
	const [files, setFiles] = useState([]);
	const [folders, setFolders] = useState([]);
	const [currentFolderId, setCurrentFolderId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showFolderModal, setShowFolderModal] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [newFolderName, setNewFolderName] = useState("");
	const [toast, setToast] = useState(null);
	const [folderPath, setFolderPath] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [previewFile, setPreviewFile] = useState(null);
	const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
	const [textContent, setTextContent] = useState(null);
	const [docxContent, setDocxContent] = useState(null);
	const [renamingFile, setRenamingFile] = useState(null);
	const [newFileName, setNewFileName] = useState("");
	const [renamingFolder, setRenamingFolder] = useState(null);
	const [renamingFolderName, setRenamingFolderName] = useState("");
	const [movingFile, setMovingFile] = useState(null);
	const [allFolders, setAllFolders] = useState([]);

	useEffect(() => {
		loadFiles();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- load when folder changes only
	}, [currentFolderId]);

	useEffect(() => {
		return () => {
			if (previewBlobUrl) {
				URL.revokeObjectURL(previewBlobUrl);
			}
		};
	}, [previewBlobUrl]);

	const loadFiles = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/files`, {
				params: { folder_id: currentFolderId || null },
			});
			console.log("Files response:", response.data);
			setFiles(response.data.files || []);
			setFolders(response.data.folders || []);
		} catch (error) {
			console.error("Error loading files:", error);
			console.error("Error response:", error.response?.data);
			const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || "Error loading files";
			setToast({ message: errorMessage, type: "error" });
			// Set empty arrays on error to prevent UI issues
			setFiles([]);
			setFolders([]);
		} finally {
			setLoading(false);
		}
	};

	const loadAllFoldersForMove = async () => {
		try {
			// Get all folders by making a request to root and building a tree
			const response = await axios.get(`${API_URL}/files`, {
				params: { folder_id: null },
			});

			const allFoldersList = [];
			const dbData = response.data;

			// Helper function to build folder path
			const getFolderPath = (folderId, folders, path = []) => {
				const folder = folders.find((f) => f.id === folderId);
				if (!folder) return path;
				if (folder.parent_id === null) {
					return [folder, ...path];
				}
				return getFolderPath(folder.parent_id, folders, [folder, ...path]);
			};

			// Get all folders recursively
			const getAllFolders = (parentId = null, folders = []) => {
				const children = dbData.folders.filter((f) => f.parent_id === parentId);
				children.forEach((folder) => {
					const path = getFolderPath(folder.id, dbData.folders);
					const displayPath = path.map((f) => f.name).join(" / ");
					allFoldersList.push({
						...folder,
						displayPath: displayPath || folder.name,
					});
					getAllFolders(folder.id, folders);
				});
			};

			getAllFolders();
			setAllFolders(allFoldersList);
		} catch (error) {
			console.error("Error loading all folders:", error);
			setAllFolders([]);
		}
	};

	const handleFileUpload = async (e) => {
		e.preventDefault();
		if (selectedFiles.length === 0) {
			setToast({ message: "Please select at least one file", type: "error" });
			return;
		}

		try {
			const formData = new FormData();
			Array.from(selectedFiles).forEach((file) => {
				formData.append("files", file);
			});
			if (currentFolderId) {
				formData.append("folder_id", currentFolderId);
			}

			await axios.post(`${API_URL}/files/upload`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			setToast({ message: "Files uploaded successfully", type: "success" });
			setShowUploadModal(false);
			setSelectedFiles([]);
			loadFiles();
		} catch (error) {
			console.error("Error uploading files:", error);
			setToast({ message: error.response?.data?.error || "Error uploading files", type: "error" });
		}
	};

	const handleCreateFolder = async (e) => {
		e.preventDefault();
		if (!newFolderName.trim()) {
			setToast({ message: "Folder name is required", type: "error" });
			return;
		}

		try {
			await axios.post(`${API_URL}/files/folders`, {
				name: newFolderName.trim(),
				parent_id: currentFolderId || null,
			});

			setToast({ message: "Folder created successfully", type: "success" });
			setShowFolderModal(false);
			setNewFolderName("");
			loadFiles();
		} catch (error) {
			console.error("Error creating folder:", error);
			setToast({ message: error.response?.data?.error || "Error creating folder", type: "error" });
		}
	};

	const handleDeleteFile = async (fileId) => {
		if (!window.confirm("Are you sure you want to delete this file?")) {
			return;
		}

		try {
			await axios.delete(`${API_URL}/files/${fileId}`);
			setToast({ message: "File deleted successfully", type: "success" });
			loadFiles();
		} catch (error) {
			console.error("Error deleting file:", error);
			setToast({ message: error.response?.data?.error || "Error deleting file", type: "error" });
		}
	};

	const handleDeleteFolder = async (folderId) => {
		if (!window.confirm("Are you sure you want to delete this folder and all its contents?")) {
			return;
		}

		try {
			await axios.delete(`${API_URL}/files/folders/${folderId}`);
			setToast({ message: "Folder deleted successfully", type: "success" });
			loadFiles();
		} catch (error) {
			console.error("Error deleting folder:", error);
			setToast({ message: error.response?.data?.error || "Error deleting folder", type: "error" });
		}
	};

	const handleRenameFile = async (e) => {
		e.preventDefault();
		if (!newFileName.trim()) {
			setToast({ message: "File name is required", type: "error" });
			return;
		}

		try {
			console.log("Renaming file:", renamingFile.id, "to:", newFileName.trim());
			const response = await axios.put(`${API_URL}/files/${renamingFile.id}/rename`, {
				name: newFileName.trim(),
			});
			console.log("Rename response:", response.data);
			setToast({ message: "File renamed successfully", type: "success" });
			setRenamingFile(null);
			setNewFileName("");
			loadFiles();
		} catch (error) {
			console.error("Error renaming file:", error);
			console.error("Error response:", error.response?.data);
			const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || "Error renaming file";
			setToast({ message: errorMessage, type: "error" });
		}
	};

	const handleStartRename = (file) => {
		setRenamingFile(file);
		setNewFileName(file.name);
	};

	const handleStartRenameFolder = (folder) => {
		setRenamingFolder(folder);
		setRenamingFolderName(folder.name);
	};

	const handleRenameFolder = async (e) => {
		e.preventDefault();
		if (!renamingFolderName.trim()) {
			setToast({ message: "Folder name is required", type: "error" });
			return;
		}

		try {
			await axios.put(`${API_URL}/files/folders/${renamingFolder.id}/rename`, {
				name: renamingFolderName.trim(),
			});
			setToast({ message: "Folder renamed successfully", type: "success" });
			setRenamingFolder(null);
			setRenamingFolderName("");
			loadFiles();
		} catch (error) {
			console.error("Error renaming folder:", error);
			const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || "Error renaming folder";
			setToast({ message: errorMessage, type: "error" });
		}
	};

	const handleFolderClick = (folder) => {
		setCurrentFolderId(folder.id);
		setFolderPath([...folderPath, folder]);
	};

	const handleBreadcrumbClick = (index) => {
		if (index === -1) {
			setCurrentFolderId(null);
			setFolderPath([]);
		} else {
			const newPath = folderPath.slice(0, index + 1);
			setFolderPath(newPath);
			setCurrentFolderId(newPath[newPath.length - 1].id);
		}
	};

	const handleStartMove = async (file) => {
		setMovingFile(file);
		await loadAllFoldersForMove();
	};

	const handleMoveFile = async (targetFolderId) => {
		if (!movingFile) return;

		const targetId = targetFolderId === null || targetFolderId === undefined ? null : targetFolderId;
		const currentFolderId = movingFile.folder_id === null || movingFile.folder_id === undefined ? null : movingFile.folder_id;

		if (currentFolderId === targetId) {
			setToast({ message: "File is already in this folder", type: "error" });
			setMovingFile(null);
			return;
		}

		try {
			await axios.put(`${API_URL}/files/${movingFile.id}/move`, {
				folder_id: targetId,
			});
			setToast({ message: "File moved successfully", type: "success" });
			setMovingFile(null);
			loadFiles();
		} catch (error) {
			const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || "Error moving file";
			setToast({ message: errorMessage, type: "error" });
		}
	};

	const getFileIcon = () => {
		return <FiFile className="text-2xl text-[#3c79bb]" />;
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
	};

	const handleDownloadFile = async (file) => {
		if (!file?.id) return;
		try {
			const response = await authFileFetch(getFileStreamUrl(file.id));
			if (!response.ok) {
				const errText = await response.text().catch(() => "");
				throw new Error(`Download failed (${response.status})${errText ? `: ${errText.slice(0, 200)}` : ""}`);
			}
			const blob = await response.blob();
			const objectUrl = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = objectUrl;
			a.download = file.name;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (err) {
			console.error("Download error:", err);
			setToast({ message: err.message || "Download failed", type: "error" });
		}
	};

	const getFileUrl = (filePath) => {
		if (filePath.startsWith("http")) return filePath;
		return getAssetUrl(filePath);
	};

	const handlePreviewFile = async (file) => {
		if (!file || !file.path) {
			console.error("Invalid file object:", file);
			setToast({ message: "Invalid file selected", type: "error" });
			return;
		}

		setPreviewFile(file);
		if (previewBlobUrl) {
			URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
		}
		setTextContent(null);
		setDocxContent(null);

		const fileType = getFileType(file.name);
		const fileUrl = getFileUrl(file.path);

		// For PDF files, fetch as blob via API and render via object URL.
		if (fileType === "pdf") {
			try {
				const response = await authFileFetch(getFileStreamUrl(file.id));
				if (!response.ok) {
					throw new Error(`Unable to load PDF (${response.status})`);
				}
				const buf = await response.arrayBuffer();
				const mime = response.headers.get("content-type") || "application/pdf";
				const blob = new Blob([buf], { type: mime.includes("pdf") ? mime : "application/pdf" });
				const objectUrl = URL.createObjectURL(blob);
				setPreviewBlobUrl(objectUrl);
			} catch (error) {
				console.error("Error loading PDF file:", error);
				setToast({ message: error.message || "Error loading PDF file", type: "error" });
			}
		}

		// For text files, fetch the content via API
		if (fileType === "text") {
			try {
				const response = await authFileFetch(getFileStreamUrl(file.id));
				if (!response.ok) {
					throw new Error(`Unable to load file (${response.status})`);
				}
				const content = await response.text();
				setTextContent(content);
			} catch (error) {
				console.error("Error loading text file:", error);
				setTextContent("Error loading file content");
			}
		}

		// For DOCX files, convert to HTML using mammoth
		if (fileType === "docx") {
			try {
				// Check if mammoth is available
				let mammoth;
				try {
					mammoth = await import("mammoth");
				} catch (importError) {
					console.error("Mammoth library not found. Please run: npm install mammoth", importError);
					setDocxContent(
						'<div style="padding: 20px; text-align: center;"><p style="color: #dc2626; font-weight: bold; margin-bottom: 10px;">Mammoth library not installed</p><p style="color: #6b7280;">Please run: <code style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px;">npm install mammoth</code> in the client directory</p></div>'
					);
					return;
				}

				console.log("Fetching DOCX via API stream:", getFileStreamUrl(file.id));
				console.log("File path from database:", file.path);

				// First, try to verify the file exists via API
				try {
					const verifyResponse = await axios.get(`${API_URL}/files/verify/${file.id}`);
					console.log("File verification:", verifyResponse.data);

					if (!verifyResponse.data.file.exists) {
						throw new Error(`File not found on server. Path: ${file.path}`);
					}
				} catch (verifyError) {
					console.error("File verification failed:", verifyError);
					// Continue anyway, might be a network issue
				}

				const response = await authFileFetch(getFileStreamUrl(file.id));
				console.log("Response status:", response.status, response.statusText);

				if (!response.ok) {
					const errorText = await response.text().catch(() => "");
					console.error("Response error:", errorText);
					throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}. File path: ${file.path}`);
				}

				const arrayBuffer = await response.arrayBuffer();
				console.log("File loaded, size:", arrayBuffer.byteLength);

				const sig = new Uint8Array(arrayBuffer.slice(0, 2));
				const isPkZip = sig[0] === 0x50 && sig[1] === 0x4b;
				if (!isPkZip) {
					const preview = new TextDecoder().decode(arrayBuffer.slice(0, Math.min(800, arrayBuffer.byteLength)));
					const looksLikeHtml = /<!DOCTYPE|<html[\s>]/i.test(preview);
					throw new Error(
						looksLikeHtml
							? "Server returned HTML instead of the document. If previews still fail after updating, check nginx: proxy /uploads to Node or alias the server uploads directory, and do not send SPA index.html for /uploads paths."
							: "File data is not a valid .docx (Office Open XML / ZIP). The upload may be corrupt or the file may not be a real .docx."
					);
				}

				// Convert DOCX to HTML
				// Handle both default and named exports
				const mammothModule = mammoth.default || mammoth;
				const result = await mammothModule.convertToHtml({ arrayBuffer: arrayBuffer });
				setDocxContent(result.value);

				if (result.messages && result.messages.length > 0) {
					console.warn("DOCX conversion messages:", result.messages);
				}
			} catch (error) {
				console.error("Error loading DOCX file:", error);
				console.error("Error details:", {
					message: error.message,
					stack: error.stack,
					streamUrl: getFileStreamUrl(file.id),
					filePath: file.path,
				});
				setDocxContent(
					`<div style="padding: 20px; text-align: center;"><p style="color: #dc2626; font-weight: bold; margin-bottom: 10px;">Error loading DOCX file</p><p style="color: #6b7280; margin-bottom: 10px;">${
						error.message || "Unknown error"
					}</p><p style="color: #6b7280; margin-bottom: 10px;">File path: ${
						file.path
					}</p><p style="color: #6b7280;">Please download the file to view it.</p></div>`
				);
			}
		}
	};

	const getFileType = (fileName) => {
		const ext = fileName.split(".").pop().toLowerCase();
		if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "image";
		if (ext === "pdf") return "pdf";
		if (["docx", "doc"].includes(ext)) return "docx";
		if (["txt", "md", "json", "xml", "csv"].includes(ext)) return "text";
		if (["mp4", "webm", "ogg", "mov", "avi"].includes(ext)) return "video";
		if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return "audio";
		return "other";
	};

	const renderPreview = () => {
		if (!previewFile) return null;

		const fileUrl = getFileUrl(previewFile.path);
		const fileType = getFileType(previewFile.name);

		return (
			<div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
				<div className="bg-white/95 rounded-2xl shadow-2xl border border-gray-300 max-w-6xl w-full max-h-[90vh] flex flex-col">
					{/* Preview Header */}
					<div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-300">
						<div className="flex-1 min-w-0">
							<h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{previewFile.name}</h2>
							<p className="text-sm text-gray-600 mt-1">{formatFileSize(previewFile.size)}</p>
						</div>
						<div className="flex items-center gap-2 ml-4">
							<button
								type="button"
								onClick={() => handleDownloadFile(previewFile)}
								className="px-4 py-2 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium text-sm"
							>
								<FiDownload className="inline mr-2" />
								Download
							</button>
							<button
								onClick={() => {
									setPreviewFile(null);
									if (previewBlobUrl) {
										URL.revokeObjectURL(previewBlobUrl);
										setPreviewBlobUrl(null);
									}
								}}
								className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
							>
								<FiX className="text-xl" />
							</button>
						</div>
					</div>

					{/* Preview Content */}
					<div className="flex-1 overflow-auto p-4 sm:p-6">
						{fileType === "image" && (
							<div className="flex items-center justify-center">
								<img src={fileUrl} alt={previewFile.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
							</div>
						)}
						{fileType === "pdf" &&
							(previewBlobUrl ? (
								<iframe src={previewBlobUrl} className="w-full h-[70vh] rounded-lg border border-gray-300" title={previewFile.name} />
							) : (
								<div className="flex items-center justify-center h-[70vh]">
									<div className="text-center">
										<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3c79bb] mb-4"></div>
										<p className="text-gray-600">Loading PDF...</p>
									</div>
								</div>
							))}
						{fileType === "docx" && (
							<div className="bg-white rounded-lg p-6 border border-gray-300 max-h-[70vh] overflow-auto">
								{docxContent !== null ? (
									<div
										className="prose max-w-none"
										dangerouslySetInnerHTML={{ __html: docxContent }}
										style={{
											fontFamily: "system-ui, -apple-system, sans-serif",
											lineHeight: "1.6",
											color: "#1f2937",
										}}
									/>
								) : (
									<div className="flex items-center justify-center h-[70vh]">
										<div className="text-center">
											<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3c79bb] mb-4"></div>
											<p className="text-gray-600">Loading document...</p>
										</div>
									</div>
								)}
							</div>
						)}
						{fileType === "text" && (
							<div className="bg-gray-50 rounded-lg p-4 border border-gray-300">
								{textContent !== null ? (
									<pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono bg-white p-4 rounded-lg border border-gray-300 max-h-[70vh] overflow-auto">
										{textContent}
									</pre>
								) : (
									<div className="flex items-center justify-center h-[70vh]">
										<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3c79bb]"></div>
									</div>
								)}
							</div>
						)}
						{fileType === "video" && (
							<div className="flex items-center justify-center">
								<video src={fileUrl} controls className="max-w-full max-h-[70vh] rounded-lg">
									Your browser does not support the video tag.
								</video>
							</div>
						)}
						{fileType === "audio" && (
							<div className="flex items-center justify-center">
								<audio src={fileUrl} controls className="w-full max-w-md">
									Your browser does not support the audio tag.
								</audio>
							</div>
						)}
						{fileType === "other" && (
							<div className="text-center py-12">
								<FiFile className="text-6xl text-gray-400 mx-auto mb-4" />
								<p className="text-gray-700 font-medium mb-4">Preview not available for this file type</p>
								<button
									type="button"
									onClick={() => handleDownloadFile(previewFile)}
									className="inline-flex items-center gap-2 px-6 py-3 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium"
								>
									<FiDownload />
									Download to view
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Filter files and folders based on search query
	const filteredFolders = folders.filter((folder) => folder.name.toLowerCase().includes(searchQuery.toLowerCase()));
	const filteredFiles = files.filter((file) => file.name.toLowerCase().includes(searchQuery.toLowerCase()));

	return (
		<div className="pb-8">
			{toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

			{/* Header */}
			<header className="glass-nav border-b border-white/20">
				<div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5">
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Files</h1>
							<p className="text-sm sm:text-base text-gray-700 mt-1 font-medium">Manage your important documents and folders</p>
						</div>
						<div className="flex items-center gap-2 sm:gap-3">
							<button
								onClick={() => setShowFolderModal(true)}
								className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium text-sm sm:text-base"
							>
								<FiFolderPlus className="text-base sm:text-lg" />
								<span className="hidden sm:inline">New Folder</span>
							</button>
							<button
								onClick={() => setShowUploadModal(true)}
								className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#4ca86b] text-white rounded-xl hover:bg-[#3d8a56] transition-colors font-medium text-sm sm:text-base"
							>
								<FiUpload className="text-base sm:text-lg" />
								<span className="hidden sm:inline">Upload</span>
							</button>
						</div>
					</div>
				</div>
			</header>

			{/* Breadcrumb */}
			{folderPath.length > 0 && (
				<div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-3 sm:py-4">
					<div className="glass-card rounded-xl px-4 sm:px-6 py-2 sm:py-3">
						<div className="flex items-center gap-2 text-sm sm:text-base">
							<button
								onClick={() => handleBreadcrumbClick(-1)}
								className="text-[#3c79bb] hover:text-[#306196] font-bold hover:underline transition-all"
							>
								Home
							</button>
							{folderPath.map((folder, index) => (
								<React.Fragment key={folder.id}>
									<span className="text-gray-700 font-bold">/</span>
									<button
										onClick={() => handleBreadcrumbClick(index)}
										className="text-[#3c79bb] hover:text-[#306196] font-bold hover:underline transition-all"
									>
										{folder.name}
									</button>
								</React.Fragment>
							))}
						</div>
					</div>
				</div>
			)}

			{/* Content */}
			<main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
				{/* Search Bar */}
				<div className="mb-6">
					<div className="relative">
						<FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg" />
						<input
							type="text"
							placeholder="Search files and folders..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-12 pr-4 py-3 glass-card rounded-xl border border-white/30 focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:outline-none text-gray-900 font-medium placeholder-gray-500"
						/>
					</div>
				</div>

				{loading ? (
					<div className="flex items-center justify-center py-12">
						<div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb]"></div>
					</div>
				) : (
					<div className="glass-card rounded-2xl overflow-hidden">
						{/* List header — flex avoids grid column totals > 12 wrapping Actions under Name */}
						<div className="bg-white/50 border-b border-white/30 px-4 sm:px-6 py-3 sm:py-4">
							<div className="flex items-center gap-2 sm:gap-4 text-sm font-bold text-gray-900">
								<div className="w-9 sm:w-10 shrink-0" aria-hidden />
								<div className="min-w-0 flex-1">Name</div>
								<div className="hidden sm:block w-16 lg:w-20 shrink-0 text-left">Type</div>
								<div className="hidden sm:block w-20 lg:w-24 shrink-0 text-right tabular-nums">Size</div>
								<div className="shrink-0 w-48 sm:w-52 text-right">Actions</div>
							</div>
						</div>

						{/* Folders List */}
						{filteredFolders.length > 0 && (
							<div className="divide-y divide-white/20">
								{filteredFolders.map((folder) => (
									<div
										key={folder.id}
										className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/30 transition-colors cursor-pointer group"
										onClick={() => handleFolderClick(folder)}
									>
										<div className="flex items-start sm:items-center gap-2 sm:gap-4">
											<div className="w-9 sm:w-10 shrink-0 flex justify-center pt-0.5 sm:pt-0">
												<FiFolder className="text-2xl sm:text-3xl text-[#3c79bb]" />
											</div>
											<div className="min-w-0 flex-1">
												<h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">{folder.name}</h3>
												<p className="text-xs text-gray-700 mt-0.5">{folder.file_count || 0} items</p>
											</div>
											<div className="hidden sm:flex w-16 lg:w-20 shrink-0 items-center">
												<span className="text-xs sm:text-sm text-gray-700 font-medium">Folder</span>
											</div>
											<div className="hidden sm:flex w-20 lg:w-24 shrink-0 items-center justify-end">
												<span className="text-xs sm:text-sm text-gray-700 font-medium">—</span>
											</div>
											<div className="shrink-0 w-48 sm:w-52 flex items-center justify-end gap-1 sm:gap-1.5 flex-nowrap">
												{user?.role === "admin" && (
													<>
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleStartRenameFolder(folder);
															}}
															className="p-1.5 sm:p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
															title="Rename folder"
															type="button"
														>
															<FiEdit2 className="text-base" />
														</button>
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleDeleteFolder(folder.id);
															}}
															className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
															title="Delete folder"
															type="button"
														>
															<FiTrash2 className="text-base" />
														</button>
													</>
												)}
											</div>
										</div>
									</div>
								))}
							</div>
						)}

						{/* Files List */}
						{filteredFiles.length > 0 && (
							<div className={`divide-y divide-white/20 ${filteredFolders.length > 0 ? "border-t border-white/30" : ""}`}>
								{filteredFiles.map((file) => (
									<div key={file.id} className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/30 transition-colors group">
										<div className="flex items-start sm:items-center gap-2 sm:gap-4">
											<div className="w-9 sm:w-10 shrink-0 flex justify-center pt-0.5 sm:pt-0">{getFileIcon(file.name)}</div>
											<div className="min-w-0 flex-1">
												<h3 className="text-sm sm:text-base font-bold text-gray-900 break-all sm:break-words sm:truncate">{file.name}</h3>
												<p className="text-xs text-gray-700 mt-0.5">
													Uploaded {new Date(file.created_at).toLocaleDateString()}
													<span className="sm:hidden text-gray-600">
														{" "}
														· {file.name.split(".").pop().toUpperCase()}
														{" · "}
														{formatFileSize(file.size)}
													</span>
												</p>
											</div>
											<div className="hidden sm:flex w-16 lg:w-20 shrink-0 items-center">
												<span className="text-xs sm:text-sm text-gray-700 font-medium">{file.name.split(".").pop().toUpperCase()}</span>
											</div>
											<div className="hidden sm:flex w-20 lg:w-24 shrink-0 items-center justify-end tabular-nums">
												<span className="text-xs sm:text-sm text-gray-700 font-medium">{formatFileSize(file.size)}</span>
											</div>
											<div className="shrink-0 w-48 sm:w-52 flex items-center justify-end gap-1 sm:gap-1.5 flex-nowrap">
												<button
													type="button"
													onClick={() => handlePreviewFile(file)}
													className="p-1.5 sm:p-2 text-[#3c79bb] hover:bg-[#3c79bb]/10 rounded-lg transition-colors"
													title="View"
													aria-label="View file"
												>
													<FiEye className="text-base" />
												</button>
												<button
													type="button"
													className="p-1.5 sm:p-2 text-[#4ca86b] hover:bg-[#4ca86b]/10 rounded-lg transition-colors"
													title="Download"
													aria-label="Download file"
													onClick={(e) => {
														e.stopPropagation();
														handleDownloadFile(file);
													}}
												>
													<FiDownload className="text-base" />
												</button>
												{user?.role === "admin" && (
													<>
														<button
															type="button"
															onClick={() => handleStartMove(file)}
															className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
															title="Move file"
														>
															<FiMove className="text-base" />
														</button>
														<button
															type="button"
															onClick={() => handleStartRename(file)}
															className="p-1.5 sm:p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
															title="Rename file"
														>
															<FiEdit2 className="text-base" />
														</button>
														<button
															type="button"
															onClick={() => handleDeleteFile(file.id)}
															className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
															title="Delete file"
														>
															<FiTrash2 className="text-base" />
														</button>
													</>
												)}
											</div>
										</div>
									</div>
								))}
							</div>
						)}

						{/* Empty State */}
						{filteredFolders.length === 0 && filteredFiles.length === 0 && (
							<div className="p-8 sm:p-12 text-center">
								<FiFolder className="text-5xl sm:text-6xl text-gray-400 mx-auto mb-4" />
								<h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
									{searchQuery ? "No results found" : "No Files or Folders"}
								</h2>
								<p className="text-gray-900 text-sm sm:text-base font-semibold mb-4">
									{searchQuery ? `No files or folders match "${searchQuery}"` : "Upload files or create folders to get started."}
								</p>
							</div>
						)}
					</div>
				)}
			</main>

			{/* Preview Modal */}
			{renderPreview()}

			{/* Rename File Modal */}
			{renamingFile && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
					<div className="bg-white/95 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-300">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900">Rename File</h2>
							<button
								onClick={() => {
									setRenamingFile(null);
									setNewFileName("");
								}}
								className="text-gray-500 hover:text-gray-700"
							>
								<FiX className="text-xl" />
							</button>
						</div>
						<form onSubmit={handleRenameFile}>
							<div className="mb-6">
								<label className="block text-sm font-bold text-gray-900 mb-2">File Name</label>
								<input
									type="text"
									value={newFileName}
									onChange={(e) => setNewFileName(e.target.value)}
									placeholder="Enter new file name"
									required
									className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
									autoFocus
								/>
								<p className="text-xs text-gray-600 mt-2">Keep the file extension (e.g., .docx, .pdf)</p>
							</div>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => {
										setRenamingFile(null);
										setNewFileName("");
									}}
									className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
								>
									Cancel
								</button>
								<button
									type="submit"
									className="flex-1 px-4 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium"
								>
									Rename
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Rename Folder Modal */}
			{renamingFolder && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
					<div className="bg-white/95 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-300">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900">Rename Folder</h2>
							<button
								onClick={() => {
									setRenamingFolder(null);
									setRenamingFolderName("");
								}}
								className="text-gray-500 hover:text-gray-700"
							>
								<FiX className="text-xl" />
							</button>
						</div>
						<form onSubmit={handleRenameFolder}>
							<div className="mb-6">
								<label className="block text-sm font-bold text-gray-900 mb-2">Folder Name</label>
								<input
									type="text"
									value={renamingFolderName}
									onChange={(e) => setRenamingFolderName(e.target.value)}
									placeholder="Enter new folder name"
									required
									className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
									autoFocus
								/>
							</div>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => {
										setRenamingFolder(null);
										setRenamingFolderName("");
									}}
									className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
								>
									Cancel
								</button>
								<button
									type="submit"
									className="flex-1 px-4 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium"
								>
									Rename
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Move File Modal */}
			{movingFile && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
					<div className="bg-white/95 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-300 max-h-[80vh] flex flex-col">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900">Move File</h2>
							<button onClick={() => setMovingFile(null)} className="text-gray-500 hover:text-gray-700">
								<FiX className="text-xl" />
							</button>
						</div>
						<div className="mb-4">
							<p className="text-sm text-gray-700 font-medium">
								Moving: <span className="font-bold text-gray-900">{movingFile.name}</span>
							</p>
						</div>
						<div className="flex-1 overflow-y-auto mb-6">
							<div className="space-y-2">
								<button
									onClick={() => handleMoveFile(null)}
									className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
										movingFile.folder_id === null
											? "bg-[#3c79bb]/20 border-2 border-[#3c79bb]"
											: "bg-white/80 border border-gray-300 hover:bg-white/90"
									}`}
								>
									<div className="flex items-center gap-3">
										<FiFolder className="text-[#3c79bb] text-lg" />
										<span className="font-medium text-gray-900">Home (Root)</span>
									</div>
								</button>
								{allFolders
									.filter((folder) => folder.id !== movingFile.folder_id)
									.map((folder) => (
										<button
											key={folder.id}
											onClick={() => handleMoveFile(folder.id)}
											className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
												movingFile.folder_id === folder.id
													? "bg-[#3c79bb]/20 border-2 border-[#3c79bb]"
													: "bg-white/80 border border-gray-300 hover:bg-white/90"
											}`}
										>
											<div className="flex items-center gap-3">
												<FiFolder className="text-[#3c79bb] text-lg" />
												<div className="flex-1">
													<span className="font-medium text-gray-900 block">{folder.name}</span>
													{folder.displayPath && folder.displayPath !== folder.name && (
														<span className="text-xs text-gray-600 mt-1">{folder.displayPath}</span>
													)}
												</div>
											</div>
										</button>
									))}
								{allFolders.filter((folder) => folder.id !== movingFile.folder_id).length === 0 && (
									<p className="text-sm text-gray-600 text-center py-4">No other folders available</p>
								)}
							</div>
						</div>
						<div className="flex gap-3">
							<button
								type="button"
								onClick={() => setMovingFile(null)}
								className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Upload Modal */}
			{showUploadModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
					<div className="bg-white/95 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-300">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900">Upload Files</h2>
							<button
								onClick={() => {
									setShowUploadModal(false);
									setSelectedFiles([]);
								}}
								className="text-gray-500 hover:text-gray-700"
							>
								<FiX className="text-xl" />
							</button>
						</div>
						<form onSubmit={handleFileUpload}>
							<div className="mb-6">
								<label className="block text-sm font-bold text-gray-900 mb-2">Select Files</label>
								<input
									type="file"
									multiple
									onChange={(e) => setSelectedFiles(e.target.files)}
									className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196]"
								/>
								{selectedFiles.length > 0 && (
									<div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
										<p className="text-sm text-green-800 font-medium">{selectedFiles.length} file(s) selected</p>
									</div>
								)}
							</div>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => {
										setShowUploadModal(false);
										setSelectedFiles([]);
									}}
									className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
								>
									Cancel
								</button>
								<button
									type="submit"
									className="flex-1 px-4 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium"
								>
									Upload
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* Create Folder Modal */}
			{showFolderModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
					<div className="bg-white/95 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-300">
						<div className="flex items-center justify-between mb-6">
							<h2 className="text-xl sm:text-2xl font-bold text-gray-900">Create Folder</h2>
							<button
								onClick={() => {
									setShowFolderModal(false);
									setNewFolderName("");
								}}
								className="text-gray-500 hover:text-gray-700"
							>
								<FiX className="text-xl" />
							</button>
						</div>
						<form onSubmit={handleCreateFolder}>
							<div className="mb-6">
								<label className="block text-sm font-bold text-gray-900 mb-2">Folder Name</label>
								<input
									type="text"
									value={newFolderName}
									onChange={(e) => setNewFolderName(e.target.value)}
									placeholder="Enter folder name"
									required
									className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
								/>
							</div>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => {
										setShowFolderModal(false);
										setNewFolderName("");
									}}
									className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
								>
									Cancel
								</button>
								<button
									type="submit"
									className="flex-1 px-4 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-medium"
								>
									Create
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default Files;
